import { FormErrorDisplay } from "@/components/form/form-error-display";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import UnderlineLink from "@/components/link/underline-link";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { Policy } from "@/pages/welcome-screen/policy";
import { Term } from "@/pages/welcome-screen/term";
import { AUTH_ACTIONS_LAYOUT_CLASS, AUTH_FORM_LAYOUT_CLASS } from "@/pages/auth-layout";
import { AUTH_CTA_CLASS, AUTH_LINK_CLASS, AuthScreenLayout } from "@/pages/auth-screen-layout";
import { APP_ROUTES } from "@/constants/app-routes";
import { useToast } from "@/hooks/ui/use-toast";
import { useLogin } from "@/hooks/use-login";
import { resolvePostAuthRoute } from "@/utils/auth/resolve-post-auth-route";
import { LoginFormValues, LoginSchema } from "@/schemas/login-schema";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter } from "@ionic/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

const LoginPage = () => {
  const navigation = useIonRouter();
  const { isLoginLoading, isError, error, loginAsync } = useLogin();
  const { showError, showSuccess } = useToast();
  const [hasLoginFailed, setHasLoginFailed] = useState(false);

  const initialValues: LoginFormValues = {
    email: "",
    password: "",
  };

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(LoginSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  async function handleLogin({ email, password }: LoginFormValues) {
    try {
      const isOnboardingCompleted = await loginAsync({ email, password });
      setHasLoginFailed(false);
      showSuccess("Login successful");
      // Navigate straight to the right destination (onboarding vs main app)
      // instead of bouncing through /overview and letting AuthRouteGuard
      // redirect — that bounce races this push and blanks the router outlet.
      navigation.push(resolvePostAuthRoute(isOnboardingCompleted), "forward", "replace");
    } catch (error) {
      setHasLoginFailed(true);
      clearErrors();

      if (error instanceof Error) {
        if (error.message.includes("Password is incorrect")) {
          showError("Wrong email / password combination.");
          return;
        }
        showError(error.message);
      } else {
        showError("An unknown error occurred");
      }
    }
  }

  const handlePasswordFocus = () => {
    if (hasLoginFailed) {
      setValue("password", "", { shouldValidate: false });
      setHasLoginFailed(false);
    }
  };

  const handleEmailBlur = () => {
    const emailValue = getValues("email");
    if (emailValue && emailValue.trim() !== "") {
      trigger("email");
    }
  };

  const handlePasswordBlur = () => {
    const passwordValue = getValues("password");
    if (passwordValue && passwordValue.trim() !== "") {
      trigger("password");
    }
  };

  return (
    <AuthScreenLayout
      title="Good to see you again"
      subtitle="Securely access your account and stay in control."
    >
      <form className={AUTH_FORM_LAYOUT_CLASS} onSubmit={handleSubmit(handleLogin)}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-[13px]">
            <TextFieldWithLabel
              control={control}
              name="email"
              label="Email"
              placeholder="Please enter your email"
              type="email"
              autocomplete="username"
              ariaLabel="Email"
              id="email"
              inputClassName="w-full"
              error={errors.email?.message}
              showErrorMessage={false}
              scrollToOnFocus
              onBlur={handleEmailBlur}
              theme="dark"
            />

            <TextFieldWithLabel
              control={control}
              name="password"
              label="Password"
              placeholder="Please enter your password"
              type="password"
              autocomplete="current-password"
              ariaLabel="Password"
              id="password"
              inputClassName="w-full"
              error={errors.password?.message}
              showErrorMessage={false}
              scrollToOnFocus
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
              theme="dark"
            />
          </div>

          <UnderlineLink
            path="/reset-password"
            text="Forgot password?"
            className={cn(textVariants.bodySmall, AUTH_LINK_CLASS, "self-center no-underline")}
          />
        </div>

        <div className={AUTH_ACTIONS_LAYOUT_CLASS}>
          <FilledButton
            disabled={!isValid || isLoginLoading}
            text="Log in"
            type="submit"
            color="white"
            textCase="normal"
            className={AUTH_CTA_CLASS}
          />

          <FormErrorDisplay
            formErrors={errors}
            apiError={isError ? error : null}
            fieldOrder={["email", "password"]}
          />

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => navigation.push(APP_ROUTES.REGISTER, "forward", "replace")}
              className={cn(
                textVariants.bodySmall,
                AUTH_LINK_CLASS,
                "mx-auto flex h-11 items-center justify-center gap-1 text-center font-medium",
              )}
            >
              <span>Don't have an account?</span>
              <span className="underline">Sign Up</span>
            </button>

            <p className={cn(textVariants.footnote, "px-2 text-center text-white")}>
              By tapping &ldquo;Log in&rdquo; or &ldquo;Create an account&rdquo;, you agree to our{" "}
              <Term className={cn(AUTH_LINK_CLASS, "font-medium")} />. Learn how we process your
              data in our <Policy className={cn(AUTH_LINK_CLASS, "font-medium")} />.
            </p>
          </div>
        </div>
      </form>
    </AuthScreenLayout>
  );
};

export default LoginPage;
